'use client';

import {
  getQueryByDifficultyLabel,
  labelToDifficulty
} from '@/app/lib/schemas/mappers/enums-to-client-mappers';
import { useCallback, useEffect, useRef, useState } from 'react';

import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useAppDispatch, useAppSelector } from '@/app/lib/store/hooks';
import { updateSearchParams } from '@/app/lib/store/search-params-slice';
import FilterDifficulty from './filter-difficulty';

export default function FilterDifficultyContainer() {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const { replace } = useRouter();

  const storedSearchParams = useAppSelector(
    state => state.searchParams.params,
    (prev, next) => JSON.stringify(prev) === JSON.stringify(next)
  );

  const { levels } = storedSearchParams;
  const dispatch = useAppDispatch();

  const levelValue = useCallback(
    (label: string) => {
      if (!storedSearchParams) return false;
      if (!storedSearchParams.levels || storedSearchParams.levels.length === 0)
        return false;

      return storedSearchParams.levels.includes(
        getQueryByDifficultyLabel(label)
      );
    },
    [storedSearchParams]
  );
  const [checkboxStates, setCheckboxStates] = useState(
    Object.fromEntries(
      [...labelToDifficulty.keys()].map(label => [label, levelValue(label)])
    )
  );

  const isInitialRender = useRef(true);

  // Обработчик изменений чекбокса
  const handleCheckboxChange = (label: string, checked: boolean) => {
    setCheckboxStates(prevState => ({
      ...prevState,
      [label]: checked
    }));
  };

  const dispatchSearchParams = useCallback(
    (levels: string[]) => {
      dispatch(
        updateSearchParams({ ...storedSearchParams, levels, page: '1' })
      );
    },
    [storedSearchParams, dispatch]
  );

  useEffect(() => {
    if (isInitialRender.current && levels) {
      setCheckboxStates(
        Object.fromEntries(
          [...labelToDifficulty.keys()].map(label => [label, levelValue(label)])
        )
      );
      isInitialRender.current = false;
    }
  }, [levels, levelValue]);

  const checkboxValuesToLevels = useCallback(() => {
    const levels = [];
    for (const label of Object.keys(checkboxStates)) {
      if (checkboxStates[label]) {
        const queryLevel = getQueryByDifficultyLabel(label);
        if (queryLevel) {
          levels.push(queryLevel);
        }
      }
    }
    return levels;
  }, [checkboxStates]);

  useEffect(() => {
    if (isInitialRender.current) {
      return;
    }
    const params = new URLSearchParams(searchParams);
    const levels = checkboxValuesToLevels();

    const oldLevels = params.getAll('levels');
    if (JSON.stringify(oldLevels) === JSON.stringify(levels)) return;

    params.delete('levels');
    params.set('page', '1');
    if (
      Object.values(checkboxStates).some(value => value === true) &&
      Object.values(checkboxStates).some(value => value === false)
    ) {
      // исключаем случаи, когда все чекбоксы выбраны или пусты

      levels.forEach(level => params.append('levels', level));

      dispatchSearchParams(levels);
    } else {
      dispatchSearchParams([]);
    }
    replace(`${pathname}?${params.toString()}`, { scroll: false });
  }, [
    checkboxStates,
    dispatch,
    replace,
    pathname,
    searchParams,
    checkboxValuesToLevels,
    dispatchSearchParams
  ]);

  return (
    <FilterDifficulty
      onCheckedChange={handleCheckboxChange}
      checkboxStates={checkboxStates}
    />
  );
}
